/**
 * The ONE rule for whether a soccer knockout "To Advance" market takes a
 * fixture's headline Moneyline slot from the regulation 1X2. Every surface
 * that answers that question calls this — the dashboard swap
 * (advanceReplacesResult), the GameDetail headline (advanceRowReplacesResult)
 * and the store's Game Lines "90 min" demotion (getAvailablePeriods) — so the
 * three can never disagree (DNL round 1, 2026-09-10: the demotion checked
 * only the first clause and listed the 1X2 as demoted while it still headed
 * the page).
 *
 * The rule: an advance market that no venue quotes is a listing, not a
 * market (Poly auto-lists `-team-to-advance` bookless for every UCL
 * league-phase fixture); and an advance frame may never remove Kalshi 1X2
 * prices from the board, so when Kalshi quotes the 1X2 the promotion needs
 * Kalshi's ADVANCE quote too (Kalshi lists none for league-phase games). A
 * Poly-only fixture keeps promoting on Poly's advance quote alone (World Cup
 * knockouts before KXWCADVANCE opened).
 */
export interface AdvancePromotionEvidence {
  /** Some venue quotes the advance market (a mid or a last trade). */
  advanceQuoted: boolean;
  /** Kalshi quotes the advance market. */
  advanceKalshiQuoted: boolean;
  /** Kalshi quotes the regulation 1X2 this advance would displace. */
  resultKalshiQuoted: boolean;
}

export function advancePromotes(e: AdvancePromotionEvidence): boolean {
  return e.advanceQuoted && (e.advanceKalshiQuoted || !e.resultKalshiQuoted);
}

/** The price a venue book carries; null = bookless listing. */
export interface PricedBook {
  priceCents: number | null;
}

/**
 * Build the evidence ONCE, in the store, from the raw advance books and the
 * 1X2's Kalshi quotes — then carry it on both the dashboard event
 * (`MultiOutcomeEvent.advancePromotion`) and the GameDetail row
 * (`GameRow.advancePromotion`). Consumers never re-derive it from a rendered
 * shape: the renderable Kalshi PAIR exists only when both ADVANCE sides have
 * arrived, but ONE priced Kalshi side is already a Kalshi quote (DNL round 2,
 * 2026-09-10 — deriving from the pair made the dashboard promote while
 * GameDetail and the "90 min" demotion did not, during a partial arrival).
 */
export function advancePromotionEvidence(
  books: {
    awayKalshi: PricedBook | null;
    homeKalshi: PricedBook | null;
    awayPoly: PricedBook | null;
    homePoly: PricedBook | null;
  },
  resultKalshiQuoted: boolean,
): AdvancePromotionEvidence {
  const priced = (b: PricedBook | null) => b != null && b.priceCents != null;
  const advanceKalshiQuoted = priced(books.awayKalshi) || priced(books.homeKalshi);
  return {
    advanceQuoted: advanceKalshiQuoted || priced(books.awayPoly) || priced(books.homePoly),
    advanceKalshiQuoted,
    resultKalshiQuoted,
  };
}
